import { createContext, FC, ReactNode, useContext } from "react";

/**
 * Primitive state hooks that each host app must implement.
 *
 * Shared hooks in `@tsmono/react` compose these primitives to build
 * higher-level hooks like `useProperty`, `useCollapsedState`, and
 * `useCollapsibleIds`.
 * Each method here is a React hook — it internally subscribes to the
 * app's store and returns reactive values.
 *
 * Adding a new member causes compile errors in both apps until they
 * supply an implementation.
 */
export interface ComponentStateHooks {
  // Property bag (persistent key-value UI state)
  useValue: (id: string, prop: string, defaultValue?: unknown) => unknown;
  useSetValue: () => (id: string, prop: string, value: unknown) => void;
  useRemoveValue: () => (id: string, prop: string) => void;
  useEntries: (id: string) => Record<string, unknown> | undefined;
  useRemoveAll: () => (id: string) => void;
  useRemoveByPrefix: () => (id: string, prefix: string) => void;
}

const ComponentStateContext = createContext<ComponentStateHooks | null>(null);

export const ComponentStateProvider: FC<{
  hooks: ComponentStateHooks;
  children: ReactNode;
}> = ({ hooks, children }) => (
  <ComponentStateContext.Provider value={hooks}>
    {children}
  </ComponentStateContext.Provider>
);

export const useComponentStateHooks = (): ComponentStateHooks => {
  const hooks = useContext(ComponentStateContext);
  if (!hooks) {
    throw new Error(
      "useComponentStateHooks must be used within a ComponentStateProvider"
    );
  }
  return hooks;
};
